import { useRef, useState } from 'react';
import type { DemoScript, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, TYPE, alpha, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { PAINTS, ThingSvg, hasVariant, isPaintable, mix, paintGap, pick, shuffle, thingDef } from './things.js';
import type { ThingId } from './things.js';

/**
 * ARCHIVIST · WHAT CHANGED? — remember the shelf, then tap the thing that changed.
 *
 * A shelf of the archive's things shows for a few seconds, a bar along the bottom running down;
 * a shutter rolls down for a moment and up again, and one thing is different. Tap it; a wrong tap
 * is a mistake (that thing fades and takes a cross). Round 1: one shelf of five things, a good
 * long look, and one is swapped for something else. Round 2: the same, with a shorter look.
 * Round 3: the same shelf of five and the same look as round 2, and one thing is swapped for
 * something else or is gone. A swap is always for a thing of another shape, never its look-alike
 * (a camera for a radio, a mug for a teapot).
 *
 * Round 3 was two shelves of six with a shorter look still, a colour changed on one and a thing gone
 * from the other, until Marek found it too hard (6 Oct 2026: "last round of the game where you have
 * to remember the shapes on the shelves was also hard"). A change of colour, and of one small part
 * (a short spout for a long one), are still in the rules; no round deals them since.
 *
 * On the kit (kit.tsx): the cabinet is drawn in the board's own 320 × 320 space; the view state
 * (`ChangedView`) holds the shelf as it stands, the shutter and the look's time.
 */

export type ChangeKind = 'swap' | 'paint' | 'gone' | 'part';

export interface ShelfItem {
  thing: ThingId;
  paint?: string | undefined;
  variant: boolean;
}

export interface Change {
  kind: ChangeKind;
  slot: number;
  /** The thing it became (swap). */
  thing?: ThingId | undefined;
  /** Its new colour (paint). */
  paint?: string | undefined;
}

export interface ShelfLevel {
  items: number;
  lookMs: number;
  /** One change per shelf, in some order. */
  kinds: readonly ChangeKind[];
  /** How many shelves the round shows, each with a different one of `kinds`; all of them if left out. */
  shelves?: number;
}

/**
 * Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2.
 * Round 3 is not the old round 2 (two shelves of six, a colour changed and a thing gone), which was
 * still too hard: it is round 2's one shelf of five, looked at as long, with a thing swapped or gone.
 */
const [ROUND_1, ROUND_2] = easier<ShelfLevel>([
  { items: 5, lookMs: 5_000, kinds: ['swap'] },
  { items: 6, lookMs: 4_300, kinds: ['paint', 'gone'] },
]);
export const SHELF_LEVELS: readonly ShelfLevel[] = [ROUND_1, ROUND_2, { ...ROUND_2, kinds: ['swap', 'gone'], shelves: 1 }];

/** What each era's shelves hold: the store room under the sand, the city's records, and so on. */
export const SHELF_POOL: Record<Era, readonly ThingId[]> = {
  stars: ['teapot', 'lamp', 'clock', 'camera', 'mug', 'bottle', 'hourglass', 'umbrella', 'radio', 'lantern', 'boot', 'apple', 'can', 'key', 'bulb'],
  neon: ['clock', 'radio', 'camera', 'lamp', 'mug', 'bottle', 'helmet', 'umbrella', 'teapot', 'hourglass', 'bulb', 'key', 'house'],
  debris: ['helmet', 'boot', 'lantern', 'radio', 'mug', 'bottle', 'clock', 'camera', 'battery', 'can', 'lamp', 'hourglass', 'teapot', 'key', 'cog'],
  dust: ['lantern', 'can', 'boot', 'bottle', 'battery', 'valve', 'hourglass', 'radio', 'mug', 'teapot', 'clock', 'lamp', 'bulb', 'cog', 'key'],
  canopy: ['can', 'teapot', 'apple', 'flower', 'tree', 'bird', 'mug', 'bottle', 'clock', 'lantern', 'house', 'umbrella', 'hourglass', 'key'],
};

/**
 * Things too alike to swap one for the other, in the same colour: a box with a round face, a pot
 * with a handle, a light under glass.
 */
const LOOK_ALIKES: readonly (readonly ThingId[])[] = [
  ['camera', 'radio'],
  ['mug', 'teapot', 'can'],
  ['lantern', 'bulb'],
];

/** The same thing, or two too alike to tell apart at a glance. */
export const lookAlike = (a: ThingId, b: ThingId): boolean => a === b || LOOK_ALIKES.some((group) => group.includes(a) && group.includes(b));

/** Things whose other part is bold enough to spot on a shelf. */
const BOLD_PARTS: readonly ThingId[] = ['teapot', 'lamp', 'mug', 'clock', 'hourglass', 'lantern', 'tree', 'house', 'umbrella', 'radio', 'boot', 'can', 'apple', 'helmet', 'bottle', 'flower'];

/** At most this many coloured things on a shelf, so a new colour is always one not on it. */
const MAX_PAINTED = PAINTS.length - 2;

/** Two colours closer than this are too alike for a change of colour (the kit's two blues). */
const NEAR = 80;

/** A shelf of `n` different things, each coloured one in a different colour. */
export function stockShelf(n: number, era: Era, rand: () => number): ShelfItem[] {
  const pool = SHELF_POOL[era];
  const things = pick(pool, n, rand);
  // Too many coloured things: trade some for plain ones (brass, metal, glass).
  const plain = shuffle(
    pool.filter((t) => !isPaintable(t) && !things.includes(t)),
    rand,
  );
  for (let i = things.length - 1; i >= 0 && things.filter(isPaintable).length > MAX_PAINTED && plain.length; i--) {
    if (isPaintable(things[i]!)) things[i] = plain.pop()!;
  }
  const paints = shuffle(PAINTS, rand);
  let k = 0;
  return things.map((thing) => ({ thing, variant: false, paint: isPaintable(thing) ? paints[k++ % paints.length] : undefined }));
}

/** Which slots a change of this kind could be made to. */
export function candidates(kind: ChangeKind, shelf: readonly ShelfItem[]): number[] {
  return shelf
    .map((item, i) => ({ item, i }))
    .filter(({ item }) =>
      kind === 'paint' ? isPaintable(item.thing) : kind === 'part' ? hasVariant(item.thing) && BOLD_PARTS.includes(item.thing) : true,
    )
    .map(({ i }) => i);
}

/** One change to a shelf. A kind that fits no slot falls back to a swap. */
export function makeChange(kind: ChangeKind, shelf: readonly ShelfItem[], era: Era, rand: () => number): Change {
  const slots = candidates(kind, shelf);
  if (slots.length === 0) return makeChange('swap', shelf, era, rand);
  const slot = slots[Math.floor(rand() * slots.length)]!;
  const used = new Set(shelf.map((s) => s.paint));
  const free = PAINTS.filter((c) => !used.has(c));
  const choices = free.length ? free : PAINTS.filter((c) => c !== shelf[slot]!.paint);
  const freePaint = () => choices[Math.floor(rand() * choices.length)]!;
  if (kind === 'swap') {
    // A thing not on the shelf, and like nothing on it, so the new one is plain to see.
    const on = new Set(shelf.map((s) => s.thing));
    const spare = SHELF_POOL[era].filter((t) => !on.has(t));
    const unlike = spare.filter((t) => !shelf.some((s) => lookAlike(t, s.thing)));
    const from = unlike.length ? unlike : spare;
    return { kind, slot, thing: from[Math.floor(rand() * from.length)]!, paint: shelf[slot]!.paint ?? freePaint() };
  }
  if (kind === 'paint') {
    // A new colour plain to see: never the old one's near twin.
    const was = shelf[slot]!.paint;
    const far = was ? choices.filter((c) => paintGap(c, was) >= NEAR) : choices;
    const from = far.length ? far : choices;
    return { kind, slot, paint: from[Math.floor(rand() * from.length)]! };
  }
  return { kind, slot };
}

/** The shelf after the change: a gone thing leaves an empty place. */
export function applyChange(shelf: readonly ShelfItem[], change: Change): (ShelfItem | null)[] {
  return shelf.map((item, i) => {
    if (i !== change.slot) return item;
    if (change.kind === 'gone') return null;
    if (change.kind === 'swap') return { thing: change.thing!, variant: false, paint: isPaintable(change.thing!) ? change.paint : undefined };
    if (change.kind === 'paint') return { ...item, paint: change.paint };
    return { ...item, variant: !item.variant };
  });
}

/** A round's shelves, each with its change. */
export function shelvesFor(level: Level, era: Era, rand: () => number): { shelf: ShelfItem[]; change: Change }[] {
  const { items, kinds, shelves } = SHELF_LEVELS[level]!;
  return shuffle(kinds, rand)
    .slice(0, shelves ?? kinds.length)
    .map((kind) => {
      const shelf = stockShelf(items, era, rand);
      return { shelf, change: makeChange(kind, shelf, era, rand) };
    });
}

/** Rows on the shelf: five as three and two, eight as four and four. */
export const rowsOf = (n: number): [number, number] => [Math.ceil(n / 2), Math.floor(n / 2)];

// The shutter's timing, on the game's clock.
const CLOSE_MS = 380;
const HIDDEN_MS = 520;
const OPEN_MS = 380;
const FOUND_MS = 1_000;
/** A wrong thing fades over this. */
const FADE_MS = 300;

type Phase = 'look' | 'close' | 'hidden' | 'open' | 'find' | 'found';

// The cabinet, in board units: two shelves, and the look's bar under it.
const W = BOARD.w;
const CAB = { x: 4, y: 6, w: W - 8, h: 278 } as const;
const SHELF_Y = [130, 264] as const;
const ITEM = 88;
const BAR = { x: 16, y: 298, w: W - 32, h: 6 } as const;

/** Where slot `i` of `n` stands: its centre x and the shelf it stands on. */
export function slotAt(i: number, n: number): { x: number; shelf: 0 | 1 } {
  const [top, bottom] = rowsOf(n);
  const row: 0 | 1 = i < top ? 0 : 1;
  const inRow = row === 0 ? top : bottom;
  const k = row === 0 ? i : i - top;
  const span = W - 40;
  return { x: 20 + (span / inRow) * (k + 0.5), shelf: row };
}

/** The place slot `i` of `n` takes on the board: where a thing stands, and a touch counts. */
export function slotBox(i: number, n: number): { x: number; y: number; w: number; h: number; size: number; base: number } {
  const { x, shelf } = slotAt(i, n);
  const base = SHELF_Y[shelf];
  const width = (W - 40) / rowsOf(n)[shelf];
  // Three to a shelf are drawn bigger than four.
  const size = Math.min(ITEM, width - 4);
  return { x: x - width / 2, y: base - size - 22, w: width, h: size + 26, size, base };
}

/** The slot under a finger at `p`, or −1. */
export function slotUnder(p: Pt, n: number): number {
  for (let i = 0; i < n; i++) {
    const b = slotBox(i, n);
    if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) return i;
  }
  return -1;
}

/** A thing on the shelf in a view state: which, its colour (an index into `PAINTS`, −1 its own) and its one part changed (1) or not. */
export type Item = readonly [thing: ThingId, paint: number, variant: number] | null;

const itemOf = (s: ShelfItem | null): Item => (s ? [s.thing, s.paint ? PAINTS.indexOf(s.paint) : -1, s.variant ? 1 : 0] : null);

/** Everything the cabinet shows. */
export interface ChangedView {
  /** The game clock, ms. */
  t: number;
  /** The shelf as it stands; null where a thing has gone. */
  i: readonly Item[];
  /** 0 look, 1 the shutter, 2 find it, 3 found. */
  s: 0 | 1 | 2 | 3;
  /** How far down the shutter is, 0–1. */
  d: number;
  /** How much of the look is left, 0–1. */
  l: number;
  /** Each place tapped wrong, 0–1 (it fades and takes a cross). */
  x: readonly number[];
  /** The place found, or −1. */
  f: number;
  /** The last tap was wrong. */
  w: boolean;
  /** Which shelf this is, and how many the round has. */
  a: number;
  of: number;
  fx?: Fx | undefined;
}

/** The archive's cabinet: a back wall, two shelf boards, lit from above. */
function Cabinet({ era, id }: { era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  const wood = era === 'neon' ? '#3a2a4a' : era === 'debris' ? '#3a2826' : '#5a3d24';
  return (
    <g>
      <defs>
        <linearGradient id={`${id}b`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={mix(skin.bg, skin.ink, 0.22)} />
          <stop offset="1" stopColor={mix(skin.bg, '#000000', 0.3)} />
        </linearGradient>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={mix(wood, '#ffffff', 0.3)} />
          <stop offset="1" stopColor={mix(wood, '#000000', 0.35)} />
        </linearGradient>
        <radialGradient id={`${id}l`} cx=".5" cy="0" r=".9">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".16" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x={CAB.x} y={CAB.y} width={CAB.w} height={CAB.h} rx="6" fill={`url(#${id}b)`} stroke={wood} strokeWidth="6" />
      <rect x={CAB.x + 4} y={CAB.y + 4} width={CAB.w - 8} height={SHELF_Y[0] - CAB.y - 4} fill={`url(#${id}l)`} />
      <rect x={CAB.x + 4} y={SHELF_Y[0] + 8} width={CAB.w - 8} height={SHELF_Y[1] - SHELF_Y[0] - 8} fill={`url(#${id}l)`} />
      {SHELF_Y.map((y) => (
        <g key={y}>
          <rect x={CAB.x} y={y} width={CAB.w} height="9" fill={`url(#${id}s)`} />
          <rect x={CAB.x} y={y + 9} width={CAB.w} height="4" fill="#000000" opacity=".35" />
        </g>
      ))}
    </g>
  );
}

/** A roller shutter that comes down over the cabinet: slats that slide, nothing that flashes. */
function Shutter({ era, down }: { era: Era; down: number }) {
  const skin = ERA_SKIN[era];
  if (down <= 0) return null;
  const h = CAB.y + CAB.h + 2;
  const slat = mix(skin.bg, skin.ink, 0.28);
  return (
    <g transform={`translate(0 ${q(-h + down * h, 1)})`}>
      <rect x={CAB.x - 2} y="0" width={CAB.w + 4} height={h} rx="6" fill={slat} />
      {Array.from({ length: 16 }, (_, i) => (
        <rect key={i} x={CAB.x - 2} y={i * (h / 16)} width={CAB.w + 4} height="2.4" fill="#000000" opacity=".35" />
      ))}
      <rect x={W / 2 - 22} y={h - 14} width="44" height="7" rx="3.5" fill={mix(skin.ink, '#000000', 0.2)} />
    </g>
  );
}

/** The cabinet, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<ChangedView>) {
  const id = useSvgId();
  const n = view.i.length;
  const found = Math.round(view.f);
  const status =
    view.s === 3
      ? { text: say(lang, '✓ FOUND IT', '✓ FUNNET'), tone: 'ok' as const }
      : view.s === 2
        ? view.w
          ? { text: say(lang, 'NOT THAT ONE', 'IKKE DEN'), tone: 'bad' as const }
          : { text: say(lang, 'WHAT CHANGED?', 'HVA ER ENDRET?'), hint: say(lang, 'TAP IT', 'TRYKK PÅ DET') }
        : view.s === 1
          ? { text: say(lang, 'SOMETHING CHANGES…', 'NOE ENDRES…') }
          : {
              text: say(lang, 'REMEMBER THE SHELF', 'HUSK HYLLA'),
              hint: view.of > 1 ? say(lang, `SHELF ${Math.round(view.a) + 1} OF ${view.of}`, `HYLLE ${Math.round(view.a) + 1} AV ${view.of}`) : undefined,
            };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <Cabinet era={era} id={id} />
      {view.i.map((item, i) => {
        const b = slotBox(i, n);
        const cx = b.x + b.w / 2;
        const wrong = clamp(view.x[i] ?? 0, 0, 1);
        const sit = item ? ((90 - (thingDef(item[0]).foot ?? 90)) / 100) * b.size : 0;
        return (
          <g key={i}>
            {found === i && (
              <rect x={b.x + 2} y={b.base - b.size - 18} width={b.w - 4} height={b.size + 16} rx="8" fill={alpha(OK, 0.13)} stroke={OK} strokeWidth="2.5" />
            )}
            <g opacity={q(1 - 0.65 * wrong, 2)}>
              {item ? (
                <>
                  <ellipse cx={cx} cy={b.base - 2} rx={b.size * 0.34} ry="3.2" fill="#000000" opacity=".45" />
                  <ThingSvg
                    thing={item[0]}
                    paint={item[1] >= 0 ? PAINTS[Math.round(item[1])] : undefined}
                    variant={item[2] > 0.5}
                    x={cx - b.size / 2}
                    y={b.base - b.size * 0.92 + sit}
                    width={b.size}
                    height={b.size}
                  />
                </>
              ) : (
                <ellipse cx={cx} cy={b.base - 2} rx={b.size * 0.3} ry="2.6" fill="#000000" opacity=".25" />
              )}
            </g>
            {wrong > 0.5 && (
              <text x={cx} y={b.base - b.size - 6} textAnchor="middle" fontFamily={mono} fontSize={TYPE.button} fontWeight="700" fill={BAD}>
                ✕
              </text>
            )}
          </g>
        );
      })}
      <Shutter era={era} down={clamp(view.d, 0, 1)} />
      {/* how long the look lasts */}
      <rect x={BAR.x} y={BAR.y} width={BAR.w} height={BAR.h} rx={BAR.h / 2} fill={INK.faint} opacity=".35" />
      {view.s === 0 && view.l > 0 && <rect x={BAR.x} y={BAR.y} width={q(BAR.w * clamp(view.l, 0, 1), 1)} height={BAR.h} rx={BAR.h / 2} fill={accent} opacity=".85" />}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, era, lang } = props;
  const [shelves] = useState(() => shelvesFor(level, era, seeded(Date.now() + level * 7919)));
  const { lookMs } = SHELF_LEVELS[level]!;
  const st = useRef<{ at: number; phase: Phase; since: number | null; changed: boolean }>({ at: 0, phase: 'look', since: null, changed: false });
  /** The places tapped wrong on this shelf, and when. */
  const wrong = useRef(new Map<number, number>());
  const lastWrong = useRef(false);

  const go = (phase: Phase, now: number) => {
    st.current.phase = phase;
    st.current.since = now;
  };

  const round = useRound(props, (_dt, now, won) => {
    const s = st.current;
    if (s.since === null) s.since = now;
    if (won) return;
    const t = now - s.since;
    if (s.phase === 'look' && t >= lookMs) go('close', now);
    else if (s.phase === 'close' && t >= CLOSE_MS) {
      s.changed = true;
      go('hidden', now);
    } else if (s.phase === 'hidden' && t >= HIDDEN_MS) go('open', now);
    else if (s.phase === 'open' && t >= OPEN_MS) go('find', now);
    else if (s.phase === 'found' && t >= FOUND_MS && s.at + 1 < shelves.length) {
      s.at += 1;
      s.changed = false;
      wrong.current = new Map();
      lastWrong.current = false;
      go('look', now);
    }
  });

  const tap = (p: Pt) => {
    const s = st.current;
    if (!round.live || s.phase !== 'find') return;
    const { shelf, change } = shelves[s.at]!;
    const slot = slotUnder(p, shelf.length);
    if (slot < 0 || wrong.current.has(slot)) return;
    const now = round.time();
    if (slot === change.slot) {
      lastWrong.current = false;
      go('found', now);
      round.hit();
      round.progress((s.at + 1) / shelves.length);
      if (s.at + 1 >= shelves.length) round.win();
      return;
    }
    wrong.current.set(slot, now);
    lastWrong.current = true;
    round.miss();
  };

  const now = round.now;
  const s = st.current;
  const t = s.since === null ? 0 : now - s.since;
  const { shelf, change } = shelves[s.at]!;
  const shown = s.changed ? applyChange(shelf, change) : shelf;
  const view: ChangedView = {
    t: q(now),
    i: shown.map(itemOf),
    s: s.phase === 'look' ? 0 : s.phase === 'find' ? 2 : s.phase === 'found' ? 3 : 1,
    d: q(s.phase === 'close' ? clamp(t / CLOSE_MS, 0, 1) : s.phase === 'hidden' ? 1 : s.phase === 'open' ? 1 - clamp(t / OPEN_MS, 0, 1) : 0, 2),
    l: s.phase === 'look' ? q(1 - clamp(t / lookMs, 0, 1), 3) : 0,
    x: shelf.map((_, i) => {
      const when = wrong.current.get(i);
      return when === undefined ? 0 : q(clamp((now - when) / FADE_MS, 0, 1), 2);
    }),
    f: s.phase === 'found' ? change.slot : -1,
    w: lastWrong.current,
    a: s.at,
    of: shelves.length,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the thing that changed', 'Trykk på det som er endret'), down: tap }}
    />
  );
}

const DEMO_TAP = (() => {
  const b = slotBox(1, 5);
  return { x: q(b.x + b.w / 2), y: q(b.base - b.size / 2) };
})();

/**
 * The how-to, on round 1's shelf of five: a good look while the bar runs down, the shutter rolls
 * down and up, and the mug in the middle is a clock now; the finger taps it.
 */
const demo: DemoScript<ChangedView> = {
  ms: 7_000,
  keys: [
    {
      at: 0,
      view: {
        t: 0,
        i: [['teapot', 0, 0], ['mug', 2, 0], ['lamp', 4, 0], ['key', -1, 0], ['boot', 1, 0]],
        s: 0,
        d: 0,
        l: 1,
        x: [0, 0, 0, 0, 0],
        f: -1,
        w: false,
        a: 0,
        of: 1,
      },
    },
    { at: 2_400, view: { l: 0 } },
    { at: 2_410, view: { s: 1 }, ease: 'step' },
    { at: 2_410 + CLOSE_MS, view: { d: 1 } },
    { at: 2_800, view: { i: [['teapot', 0, 0], ['clock', 2, 0], ['lamp', 4, 0], ['key', -1, 0], ['boot', 1, 0]] }, ease: 'step' },
    { at: 3_320, view: { d: 1 } },
    { at: 3_320 + OPEN_MS, view: { d: 0 } },
    { at: 3_710, view: { s: 2 }, ease: 'step' },
    { at: 4_500, view: { s: 3, f: 1, fx: [1, 0] }, ease: 'step' },
    { at: 5_000, view: { fx: [0, 0] } },
  ],
  finger: [
    { at: 0, x: 280, y: 300, hide: true },
    { at: 3_500, x: 280, y: 300, hide: false },
    { at: 4_200, x: DEMO_TAP.x, y: DEMO_TAP.y },
    { at: 4_500, x: DEMO_TAP.x, y: DEMO_TAP.y, down: true },
    { at: 4_650, x: DEMO_TAP.x, y: DEMO_TAP.y, down: false },
    { at: 5_400, x: 280, y: 300 },
    { at: 5_600, x: 280, y: 300, hide: true },
  ],
  steps: [
    { at: 0, en: 'REMEMBER THE SHELF', no: 'HUSK HYLLA' },
    { at: 2_400, en: 'ONE THING CHANGES', no: 'EN TING ENDRES' },
    { at: 3_700, en: 'TAP WHAT CHANGED', no: 'TRYKK PÅ DET SOM ER ENDRET' },
  ],
};

export const game = thirtyGame<ChangedView>({
  Board,
  View,
  demo,
  words: {
    en: {
      instruction: 'Remember the shelf. After the shutter, tap the one thing that changed.',
      howMove: 'Look hard while the shelf is open. When it opens again, tap what is different.',
    },
    no: {
      instruction: 'Husk hylla. Når luken går opp igjen, trykk på det ene som er endret.',
      howMove: 'Se nøye mens hylla er åpen. Når den åpnes igjen, trykk på det som er annerledes.',
    },
  },
});
